<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
      </div>
      <div>
        <b-field grouped>
          <b-field label="Start Date">
            <b-datepicker
              v-model="dateStart"
              name="dateStart"
              :first-day-of-week="1"
              :locale="'en-CA'"
              icon="calendar-alt"
              placeholder="Start Date"
            />
          </b-field>
          <b-field label="End Date">
            <b-datepicker
              v-model="dateEnd"
              name="dateEnd"
              :first-day-of-week="1"
              :locale="'en-CA'"
              icon="calendar-alt"
              placeholder="End Date"
            />
          </b-field>
          <b-field v-if="user.group.plants && user.group.plants.length != 0" label="Plant">
            <b-select v-model="selectedPlant" icon="industry" expanded>
              <option v-for="plant in user.group.plants" :key="plant.id" :value="plant">
                {{ plant.name }}
              </option>
            </b-select>
          </b-field>
          <b-field v-else label="Plant">
            <multiselect
              v-model="selectedPlant"
              :options="plants"
              :multiple="false"
              :close-on-select="true"
              placeholder="Pick plant"
              :custom-label="(obj) => obj.id + ' - ' + obj.name"
              track-by="name"
              style="min-width: 400px;"
            />
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Types">
            <multiselect
              v-model="selectedTypes"
              :options="siteTypes"
              :multiple="true"
              :close-on-select="false"
              placeholder="Pick Types to forecast"
              :custom-label="(obj) => obj.name"
              track-by="name"
              style="min-width: 400px;"
            />
          </b-field>
        </b-field>
        <b-field grouped>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
            <b-button
              class="left-right ml-3"
              type="is-success"
              icon-left="download"
              :disabled="!forecasts.length > 0"
              @click="() => {exportToExcel()}"
            >
              Export xlsx
            </b-button>
            <b-button
              class="left-right ml-3"
              type="is-success"
              icon-left="upload"
              :disabled="isLoading || !forecasts.length > 0"
              @click="() => {isImportingExcel = true}"
            >
              Import xlsx
            </b-button>
            <b-button
              class="left-right ml-3"
              type="is-warning"
              icon-left="x"
              :disabled="!forecasts.length > 0"
              @click="() => {resetSimulations()}"
            >
              Reset Simulations
            </b-button>
            <div style="margin-left: 10px; margin-right: 10px; display: flex; flex-direction: column;">
              <b-switch v-model="shipmentView">
                Shipment View
              </b-switch>
              <b-switch v-model="infoCard" style="margin-top: 5px;">
                Info Card
              </b-switch>
            </div>
          </b-field>
          <b-field v-if="undecidedCollsOut.length > 0 || undecidedCollsIn.length > 0">
            <b-button
              class="left-right"
              type="is-success"
              icon-left="box"
              @click="() => {isCheckingUndecided = true}"
            >
              Undated Shipments
            </b-button>
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Max Stocks" class="card px-2 py-2" grouped>
            <b-field>
              {{ "Max total: " + target.max_total }}
            </b-field>
            <b-field>
              {{ "Max Clean: " + target.max_clean }}
            </b-field>
          </b-field>
          <b-field v-if="forecasts.length > 0" label="Safety Stocks" class="card px-2 py-2" grouped>
            <b-field>
              {{ "Clean: " + target.min_clean }}
            </b-field>
            <b-field>
              {{ "Dirty: " + target.min_dirty }}
            </b-field>
            <b-field>
              {{ "Available: " + target.min_available }}
            </b-field>
            <b-field>
              {{ "Repair: " + target.min_repair }}
            </b-field>
          </b-field>
        </b-field>
      </div>
      <div v-if="forecasts.length > 0 && infoCard" class="card px-2 py-2" style="width: fit-content; position: fixed; top: 85px; right: 5px; z-index: 3;">
        <b-field> {{ selectedPlant.name }} </b-field>
        <b-field>
          <b-switch v-model="shipmentView">
            Shipment View
          </b-switch>
        </b-field>
        <b-field v-if="forecasts.length > 0" label="Max Stocks" grouped>
          <b-field>
            {{ "Max total: " + target.max_total }}
          </b-field>
          <b-field>
            {{ "Max Clean: " + target.max_clean }}
          </b-field>
        </b-field>
        <b-field v-if="forecasts.length > 0" label="Safety Stocks" grouped>
          <b-field>
            {{ "Clean: " + target.min_clean }}
          </b-field>
          <b-field>
            {{ "Dirty: " + target.min_dirty }}
          </b-field>
          <b-field>
            {{ "Available: " + target.min_available }}
          </b-field>
          <b-field>
            {{ "Repair: " + target.min_repair }}
          </b-field>
        </b-field>
        <b-button
          class="left-right mr-2"
          type="is-ghost"
          icon-left="x"
          :style="{'display': 'block', 'position': 'absolute', 'top': '0', 'right': '0'}"
          @click="() => {closeInfoCard()}"
        />
      </div>
      <div
        v-for="(forecast, index) in forecasts"
        :key="forecast.date"
        class="card pl-2 py-1"
        :style="{
          'margin-top': '2rem',
          'padding-right': '0.75rem',
          'background-color': forecast.inventory ? 'rgb(252, 252, 149)' : '',
          'border': '2px solid transparent',
          'border-image-slice': '1',
          'border-image': 'linear-gradient(to left, rgb(125, 125, 125), rgb(225, 225, 225)) 3'
        }"
      >
        <div :style="{'text-transform': 'capitalize'}">
          {{ new Date(forecast.date).toLocaleDateString("en-EN", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}
        </div>
        <div v-if="!shipmentView" class="columns">
          <div
            v-for="type in selectedTypes"
            :key="type.name"
            class="column"
          >
            <div style="border-bottom: 1px rgb(100, 100, 100) solid; padding-left: 0.3rem;">
              {{ type.name }}
            </div>
            <div
              v-for="status in forecast.statuses"
              :key="status"
              style="background-color: rgba(245, 222, 179, 0.8); padding-left: 0.3rem;"
            >
              {{ status }}
              <span :style="forecast.values[type.name][status].num_crates >= 0 ? forecast.values[type.name][status].changed ? {color: 'blue'} : {} : {color: 'red'}">
                {{ forecast.values[type.name][status].num_crates }}
              </span>
            </div>
            <div
              :style="{
                'border-top': '1px rgb(190, 190, 190) solid',
                'background-color': 'rgba(190, 230, 250, 1)',
                'padding-left': '0.3rem'
              }"
            >
              Washed <span
                :style="{
                  'color': forecast.washed[type.name] ? 'black' : simulations[forecast.date + '__' + 'washing' + '__' + type.name] ? 'rgb(76, 160, 76)' : 'blue'
                }"
              >
                {{ forecast.washed[type.name] !== null ? forecast.washed[type.name] : simulations[forecast.date + '__' + 'washing' + '__' + type.name] ? simulations[forecast.date + '__' + 'washing' + '__' + type.name] : new Date(forecast.date).getDay() === 6 || new Date(forecast.date).getDay() === 0 || !(new Date(forecast.date) > new Date().setDate(new Date().getDate() - 5)) ? 0 : (crateMeans[type.name + '__' + 'washing'] + crateMeans[type.name + '__' + 'machineWash'])?.toFixed(1) }}
              </span>
            </div>
            <div
              :style="{
                'background-color': 'rgba(190, 230, 250, 1)',
                'padding-left': '0.3rem'
              }"
            >
              Repaired <span :style="forecast.repaired[type.name] ? {} : simulations[forecast.date + '__' + 'repair' + '__' + type.name] ? {'color': 'rgb(76, 160, 76)'} : {'color': 'blue'}">{{ forecast.repaired[type.name] ? forecast.repaired[type.name] : simulations[forecast.date + '__' + 'repair' + '__' + type.name] ? simulations[forecast.date + '__' + 'repair' + '__' + type.name] : new Date(forecast.date).getDay() === 6 || new Date(forecast.date).getDay() === 0 || !(new Date(forecast.date) > new Date().setDate(new Date().getDate() - 5)) ? 0 : crateMeans[type.name + '__' + 'repair']?.toFixed(1) }}</span>
            </div>
            <div
              :style="{
                'cursor': 'pointer',
                'border-top': '1px rgb(190, 190, 190) solid',
                'background-color': 'rgba(180, 250, 160, 1)',
                'padding-left': '0.3rem'
              }"
              @click="() => {collsInDisplayed = true; checkCollects(crateIOs[type.name][index])}"
            >
              In <span :style="crateIOs[type.name][index].doneCollsIn.length > 0 ? {} : crateIOs[type.name][index].simIn ? {'color': 'green'} : new Date(forecast.date) > new Date().setDate(new Date().getDate() + 1) ? (crateIOs[type.name][index].totalIn >= collMeans[type.name] || new Date(forecast.date).getDay() === 6 || new Date(forecast.date).getDay() === 0) ? {'color': 'blue'} : {'color': 'brown'} : crateIOs[type.name][index].predCollsIn.length > 0 ? {'color': 'blue'} : {}">{{ new Date(forecast.date) > new Date().setDate(new Date().getDate() + 1) ? (crateIOs[type.name][index].totalIn >= collMeans[type.name] || new Date(forecast.date).getDay() === 6 || new Date(forecast.date).getDay() === 0) ? crateIOs[type.name][index].totalIn : collMeans[type.name]?.toFixed(1) : crateIOs[type.name][index].totalIn }}</span>
            </div>
            <div
              :style="{
                'cursor': 'pointer',
                'background-color': 'rgba(180, 250, 160, 1)',
                'padding-left': '0.3rem'
              }"
              @click="() => {collsInDisplayed = false; checkCollects(crateIOs[type.name][index])}"
            >
              Out <span :style="crateIOs[type.name][index].doneCollsOut.length > 0 ? {} : crateIOs[type.name][index].simOut ? {'color': 'green'} : crateIOs[type.name][index].predCollsOut.length > 0 ? {'color': 'blue'} : {}">{{ crateIOs[type.name][index].totalOut }}</span>
            </div>
          </div>
          <div class="column">
            <div :style="{ color: 'black', 'font-weight': 'bold' }">
              Max
            </div>
            <table :style="{ color: 'black', 'border-spacing': '1rem 0px', 'border-collapse': 'separate' }">
              <tr>
                <td>{{ "Total:" }}</td>
                <td>{{ forecast.total }}</td>
                <td :style="{ color: (forecast.total/target.max_total * 100) > 80 ? 'red' : 'black' }">
                  {{ target.max_total && target.max_total !== 0 ? (forecast.total/target.max_total * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Clean:" }}</td>
                <td>{{ forecast.clean }}</td>
                <td :style="{ color: (forecast.clean/target.max_clean * 100) > 80 ? 'red' : 'black' }">
                  {{ target.max_clean && target.max_clean !== 0 ? (forecast.clean/target.max_clean * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
            </table>
            <div :style="{ color: 'black', 'font-weight': 'bold' }">
              Safety
            </div>
            <table :style="{ color: 'black', 'border-spacing': '1rem 0px', 'border-collapse': 'separate' }">
              <tr>
                <td>{{ "Available:" }}</td>
                <td>{{ forecast.available }}</td>
                <td :style="{ color: (forecast.available/target.min_available * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_available && target.min_available !== 0 ? (forecast.available/target.min_available * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Clean:" }}</td>
                <td>{{ forecast.clean }}</td>
                <td :style="{ color: (forecast.clean/target.min_clean * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_clean && target.min_clean !== 0 ? (forecast.clean/target.min_clean * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Dirty:" }}</td>
                <td>{{ forecast.dirty }}</td>
                <td :style="{ color: (forecast.dirty/target.min_dirty * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_dirty && target.min_dirty !== 0 ? (forecast.dirty/target.min_dirty * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
              <tr>
                <td>{{ "Repair:" }}</td>
                <td>{{ forecast.repair }}</td>
                <td :style="{ color: (forecast.repair/target.min_repair * 100) < 100 ? 'red' : 'green' }">
                  {{ target.min_repair && target.min_repair !== 0 ? (forecast.repair/target.min_repair * 100).toFixed(0) + '%' : '' }}
                </td>
              </tr>
            </table>
          </div>
          <!-- <b-button
            class="left-right mr-2"
            type="is-ghost"
            icon-left="door-closed"
            :style="{'display': 'block', 'position': 'absolute', 'right': '60px', 'margin-top': '-10px', 'color': 'black' }"
            :disabled="isLoading"
            @click="() => {editForecast(forecast)}"
          />
          <b-button
            class="left-right mr-2"
            type="is-ghost"
            icon-left="x"
            :style="{'display': 'block', 'position': 'absolute', 'right': '60px', 'margin-top': '-10px', 'color': 'red' }"
            :disabled="isLoading"
            @click="() => {editForecast(forecast)}"
          /> -->
          <b-tooltip
            :label="forecast.comment?.text"
            :active="forecast.comment ? true : false"
            multilined
          >
            <b-button
              class="left-right mr-2"
              type="is-ghost"
              :icon-left="forecast.comment ? 'star' : 'comment'"
              :style="{'display': 'block', 'position': 'absolute', 'right': '30px', 'margin-top': '-10px', 'color': forecast.comment ? 'gold' : 'gray'}"
              :disabled="isLoading"
              @click="() => {editComment(forecast)}"
            />
          </b-tooltip>
          <b-button
            class="left-right mr-2"
            type="is-ghost"
            icon-left="edit"
            :style="{'display': 'block', 'position': 'absolute', 'right': '0', 'margin-top': '-10px'}"
            :disabled="isLoading"
            @click="() => {editForecast(forecast)}"
          />
        </div>
        <div
          v-if="shipmentView"
          :style="{
            'display': 'flex',
            'flex-direction': 'row',
            'justify-content': 'space-around'
          }"
        >
          <div
            :style="{
              'height': '15rem',
              'max-height': '15rem',
              'overflow-y': 'scroll',
              '-ms-overflow-style': 'none',
              'scrollbarWidth': '0',
              'width': '45%'
            }"
          >
            <table
              :style="{
                'display': 'flex',
                'flex-direction': 'column',
                'border': '2px solid rgb(140 140 140)',
                'width' : '100%',
                'border-spacing': '0.5rem 0px',
                'border-collapse': 'collapse',
                'text-align': 'left'
              }"
            >
              <thead style="position: sticky; top: 0; background-color: lightgray; display: table; width: 100%;">
                <tr>
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; white-space: nowrap;">
                    Origin
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    OB
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    RO
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    Nbr
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    Type
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    Status
                  </td>
                </tr>
              </thead>
              <tbody style="width: 100%; display: table;">
                <tr
                  v-for="move in forecast.dayMoves.doneCollsIn"
                  :key="'donecollsin' + move.id"
                  :style="{
                    'color': move.relic ? 'blue' : 'black',
                    'font-weight': move.naval ? 'bold' : 'regular',
                    'background-color': move.shipType === 'COL'? 'rgb(162, 200, 215)' : 'rgb(162, 200, 255)',
                    'border-top': 'solid 1px rgb(220, 220, 220)',
                    'border-left': 'solid 1px rgb(220, 220, 220)',
                    'border-right': 'solid 1px rgb(220, 220, 220)'
                  }"
                >
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ move.name }}
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    {{ move.id }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.shipment }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ parseInt(move.num_crates) }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ move.type }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.status }}
                  </td>
                </tr>
                <tr
                  v-for="move in forecast.dayMoves.predCollsIn"
                  :key="'predcollsin' + move.id"
                  :style="{
                    'color': move.relic ? 'blue' : 'black',
                    'font-weight': move.naval ? 'bold' : 'regular',
                    'background-color': move.shipType === 'COL'? 'rgb(162, 200, 215)' : 'rgb(162, 200, 255)',
                    'border-top': 'solid 1px rgb(220, 220, 220)',
                    'border-left': 'solid 1px rgb(220, 220, 220)',
                    'border-right': 'solid 1px rgb(220, 220, 220)'
                  }"
                >
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ move.name }}
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    {{ move.id }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.shipment }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ parseInt(move.num_crates) }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ move.type }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.status }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            :style="{
              'height': '15rem',
              'max-height': '15rem',
              'overflow-y': 'scroll',
              'width': '45%'
            }"
          >
            <table
              :style="{
                'display': 'flex',
                'flex-direction': 'column',
                'border': '2px solid rgb(140 140 140)',
                'width' : '100%',
                'border-spacing': '1rem 0px',
                'border-collapse': 'collapse',
                'text-align': 'left'
              }"
            >
              <thead style="position: sticky; top: 0; background-color: lightgray; display: table; width: 100%;">
                <tr>
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; white-space: nowrap;">
                    Destination
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    OB
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    RO
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    Nbr
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    Type
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    Status
                  </td>
                </tr>
              </thead>
              <tbody style="width: 100%; display: table;">
                <tr
                  v-for="move in forecast.dayMoves.doneCollsOut"
                  :key="'donecollsout' + move.id"
                  :style="{
                    'color': move.relic ? 'blue' : 'black',
                    'font-weight': move.naval ? 'bold' : 'regular',
                    'background-color': move.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                    'border-top': 'solid 1px rgb(220, 220, 220)',
                    'border-left': 'solid 1px rgb(220, 220, 220)',
                    'border-right': 'solid 1px rgb(220, 220, 220)'
                  }"
                >
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ move.name }}
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    {{ move.id }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.shipment }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ parseInt(move.num_crates) }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ move.type }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.status }}
                  </td>
                </tr>
                <tr
                  v-for="move in forecast.dayMoves.predCollsOut"
                  :key="'predcollsout' + move.id"
                  :style="{
                    'color': move.relic ? 'blue' : 'black',
                    'font-weight': move.naval ? 'bold' : 'regular',
                    'background-color': move.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                    'border-top': 'solid 1px rgb(220, 220, 220)',
                    'border-left': 'solid 1px rgb(220, 220, 220)',
                    'border-right': 'solid 1px rgb(220, 220, 220)'
                  }"
                >
                  <td style="width: 10ch; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ move.name }}
                  </td>
                  <td style="width: 10ch; max-width: 10ch;">
                    {{ move.id }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.shipment }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ parseInt(move.num_crates) }}
                  </td>
                  <td style="width: 4ch; max-width: 4ch;">
                    {{ move.type }}
                  </td>
                  <td style="width: 6ch; max-width: 6ch;">
                    {{ move.status }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <b-modal key="undecided" :active.sync="isCheckingUndecided" scroll="keep" has-modal-card>
      <div v-if="isCheckingUndecided" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Undated Movements
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <div style="display: flex; align-items: center; margin-top: -20px; margin-bottom: 20px">
              View Crates OUT
              <b-switch v-model="collsInDisplayed" style="margin-left: 10px;" />
              View Crates IN
              <div style="margin-left: auto; font-weight: bold;">
                Total: {{ collsInDisplayed ? totalUndecidedIn : totalUndecidedOut }}
              </div>
            </div>
            <div v-if="collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Origin
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
              </div>
              <div
                v-for="undecidedCollIn in undecidedCollsIn"
                :key="undecidedCollIn.id"
                class="columns"
                :style="{
                  'color': undecidedCollIn.relic ? 'blue' : 'black',
                  'font-weight': undecidedCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="undecidedCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ undecidedCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ undecidedCollIn.id }}
                </div>
                <div class="column">
                  {{ undecidedCollIn.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(undecidedCollIn.num_crates) }}
                </div>
                <div class="column">
                  {{ undecidedCollIn.type }}
                </div>
                <div class="column">
                  {{ undecidedCollIn.status }}
                </div>
              </div>
            </div>
            <div v-if="!collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Destination
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
              </div>
              <div
                v-for="undecidedCollOut in undecidedCollsOut"
                :key="undecidedCollOut.id"
                class="columns"
                :style="{
                  'color': 'black',
                  'font-weight': 'regular',
                  'background-color': undecidedCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="undecidedCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ undecidedCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ undecidedCollOut.id }}
                </div>
                <div class="column">
                  {{ undecidedCollOut.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(undecidedCollOut.num_crates) }}
                </div>
                <div class="column">
                  {{ undecidedCollOut.type }}
                </div>
                <div class="column">
                  {{ undecidedCollOut.status }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-modal>
    <b-modal :active.sync="isImportingExcel" scroll="keep" has-modal-card>
      <div v-if="isImportingExcel" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Import Excel Simulations
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field>
                <input
                  id="fileUpload"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  @change="loadFile"
                >
                <b-button class="btn" @click="chooseFiles">
                  Choose File
                </b-button>
              </b-field>
              <b-field>
                <b-button
                  class="left-right"
                  type="is-warning"
                  icon-left="x"
                  :disabled="!loadedExcel"
                  @click="() => {testExcel()}"
                >
                  Read Simulations
                </b-button>
              </b-field>
            </b-field>
            <div v-if="days && days.length > 0" style="margin-top: 5px; margin-bottom: 20px;">
              <div v-for="(day, index) in days" :key="'simulation' + index">
                <b-field grouped style="background-color: white; padding: 5px; width: fit-content;">
                  <b-field>{{ new Date(day.date).toLocaleDateString('en-CA') }}</b-field>
                  <b-field>{{ day.type }}</b-field>
                  <b-field>{{ day.operation }}</b-field>
                  <b-field>{{ day.num_crates }}</b-field>
                </b-field>
              </div>
            </div>
            <b-field>
              <b-button
                class="left-right"
                type="is-warning"
                icon-left="x"
                :disabled="days.length <= 0"
                @click="() => {saveSimulations()}"
              >
                Save Simulations
              </b-button>
            </b-field>
          </section>
        </div>
      </div>
    </b-modal>

    <b-modal
      :key="colModalKey"
      :active.sync="isCheckingColls"
      scroll="keep"
      has-modal-card
    >
      <div v-if="isCheckingColls" style="margin-left: -20vw;">
        <div class="modal-card" style="min-width: 70vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Movements
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="displayedColls.collDate" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ displayedColls.collDate }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <div style="display: flex; align-items: center; margin-top: -20px; margin-bottom: 20px">
              View Crates OUT
              <b-switch v-model="collsInDisplayed" style="margin-left: 10px;" />
              View Crates IN
            </div>
            <div v-if="collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Origin
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="doneCollIn in displayedColls.doneCollsIn"
                :key="doneCollIn.id"
                class="columns"
                :style="{
                  'color': doneCollIn.relic ? 'blue' : 'black',
                  'font-weight': doneCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="doneCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ doneCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ doneCollIn.id }}
                </div>
                <div class="column">
                  {{ doneCollIn.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(doneCollIn.num_crates) }}
                </div>
                <div class="column">
                  {{ doneCollIn.type }}
                </div>
                <div class="column">
                  {{ doneCollIn.status }}
                </div>
                <div class="column">
                  {{ doneCollIn.done_date }}
                </div>
              </div>
              <div
                v-for="predCollIn in displayedColls.predCollsIn"
                :key="predCollIn.id"
                class="columns"
                :style="{
                  'color': predCollIn.relic ? 'blue' : 'black',
                  'font-weight': predCollIn.naval ? 'bold' : 'regular',
                  'background-color': 'rgb(162, 200, 215)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="predCollIn.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ predCollIn.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ predCollIn.id }}
                </div>
                <div class="column">
                  {{ predCollIn.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(predCollIn.num_crates) }}
                </div>
                <div class="column">
                  {{ predCollIn.type }}
                </div>
                <div class="column">
                  {{ predCollIn.status }}
                </div>
                <div class="column">
                  {{ predCollIn.originDate }}
                </div>
              </div>
            </div>
            <div v-if="!collsInDisplayed">
              <div class="columns" style="background-color: rgb(255, 255, 255);">
                <div class="column">
                  Destination
                </div>
                <div class="column">
                  OB
                </div>
                <div class="column">
                  RO
                </div>
                <div class="column">
                  Quantity
                </div>
                <div class="column">
                  Type
                </div>
                <div class="column">
                  Status
                </div>
                <div class="column">
                  Date
                </div>
              </div>
              <div
                v-for="doneCollOut in displayedColls.doneCollsOut"
                :key="doneCollOut.id"
                class="columns"
                :style="{
                  'color': doneCollOut.relic ? 'blue' : 'black',
                  'font-weight': doneCollOut.naval ? 'bold' : 'regular',
                  'background-color': doneCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="doneCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ doneCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ doneCollOut.id }}
                </div>
                <div class="column">
                  {{ doneCollOut.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(doneCollOut.num_crates) }}
                </div>
                <div class="column">
                  {{ doneCollOut.type }}
                </div>
                <div class="column">
                  {{ doneCollOut.status }}
                </div>
                <div class="column">
                  {{ doneCollOut.done_date }}
                </div>
              </div>
              <div
                v-for="predCollOut in displayedColls.predCollsOut"
                :key="predCollOut.id"
                class="columns"
                :style="{
                  'color': predCollOut.relic ? 'blue' : 'black',
                  'font-weight': predCollOut.naval ? 'bold' : 'regular',
                  'background-color': predCollOut.shipType === 'DEL'? 'rgb(146, 208, 80)': 'rgb(251, 226, 213)',
                  'border-top': 'solid 1px rgb(220, 220, 220)',
                  'border-left': 'solid 1px rgb(220, 220, 220)',
                  'border-right': 'solid 1px rgb(220, 220, 220)'
                }"
              >
                <div class="column">
                  <b-tooltip :label="predCollOut.name" position="is-right">
                    <div style="overflow: hidden; text-overflow: ellipsis; text-wrap: nowrap; width: 18ch;">
                      {{ predCollOut.name }}
                    </div>
                  </b-tooltip>
                </div>
                <div class="column">
                  {{ predCollOut.id }}
                </div>
                <div class="column">
                  {{ predCollOut.shipment }}
                </div>
                <div class="column">
                  {{ parseInt(predCollOut.num_crates) }}
                </div>
                <div class="column">
                  {{ predCollOut.type }}
                </div>
                <div class="column">
                  {{ predCollOut.status }}
                </div>
                <div class="column">
                  {{ predCollOut.originDate }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </b-modal>

    <b-modal :key="'commentModal'" :active.sync="isEditingComment" scroll="keep" has-modal-card>
      <form v-if="isEditingComment" @submit.prevent="submitComment(false)">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Comment
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="editableComment.date" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ editableComment.date }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field label="Comment">
              <b-input v-model="bufferText" type="textarea" />
            </b-field>
            <b-button
              class="left-right"
              type="is-danger"
              icon-left="box"
              @click="() => {submitComment(true)}"
            >
              Delete
            </b-button>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>

    <b-modal :key="modalKey" :active.sync="isEditingForecast" scroll="keep" has-modal-card>
      <form v-if="isEditingForecast" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit performance forecast
              <span v-if="storedName" style="font-size:1rem; text-overflow: elipsis">{{ storedName }}</span>
              <span v-if="editableForecast.date" class="ml-3" style="font-size:1rem; text-overflow: elipsis">{{ editableForecast.date }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Start Date">
                <b-datepicker
                  v-model="editDateStart"
                  name="editDateStart"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Start Date"
                />
              </b-field>
              <b-field v-if="editBatch" label="End Date">
                <b-datepicker
                  v-model="editDateEnd"
                  name="editDateEnd"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="End Date"
                />
              </b-field>
              <b-field label="Multiple Days?">
                <b-checkbox v-model="editBatch" name="batchEdit" />
              </b-field>
            </b-field>
            <div class="columns">
              <div
                v-for="type in editableForecast.types"
                :key="type"
                class="column"
              >
                <div>{{ type }}</div>
                <div
                  v-for="status in editableForecast.statuses"
                  :key="status"
                >
                  {{ status }}
                  <span :style=" editableForecast.values[type][status].changed ? {color: 'blue'} : {}">
                    {{ editableForecast.values[type][status].num_crates }}
                  </span>
                </div>
                <b-field label="Washed" label-position="on-border" class="mt-1">
                  <b-input v-model="editableWashed[type]" :disabled="editableWashed[type] === null" type="number" step="0.1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableWashed[type] = null; modalKey += 1}" />
                </b-field>
                <b-field label="Repaired" label-position="on-border" class="mt-1">
                  <b-input v-model="editableRepaired[type]" :disabled="editableRepaired[type] === null" type="number" step="0.1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableRepaired[type] = null; modalKey += 1}" />
                </b-field>
                <b-field label="In" label-position="on-border" class="mt-1">
                  <b-input v-model="editableIn[type]" :disabled="editableIn[type] === null" type="number" step="0.1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableIn[type] = null; modalKey += 1}" />
                </b-field>
                <b-field label="Out" label-position="on-border" class="mt-1">
                  <b-input v-model="editableOut[type]" :disabled="editableOut[type] === null" type="number" step="0.1" />
                  <b-button icon-left="circle-xmark" type="is-warning" class="is-small" @click="() => {editableOut[type] = null; modalKey += 1}" />
                </b-field>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Plant from '../../services/v2/plant'
import Forecast from '../../services/v3/crateForecast'
import exceljs from 'exceljs'
import { saveAs } from 'file-saver'

export default {
  name: 'CrateForecast',
  components: {
  },
  data: function () {
    return {
      dateStart: new Date(),
      dateEnd: new Date(),
      isLoading: false,
      infoCard: true,
      modalKey: 0,
      colModalKey: -1,
      activeTab: '5.2',
      selectedPlant: JSON.parse(sessionStorage.getItem('consultingPlant')),
      storedPlantId: null,
      storedName: null,
      shipmentView: false,
      editDate: null,
      plants: [],
      siteTypes: [],
      selectedTypes: [],
      types: [{ name: '1' }, { name: '5.1' }, { name: '5.2' }, { name: '5.5' }, { name: '5.X' }, { name: 'FOOD' }, { name: 'ZEON' }, { name: 'RHODIA' }],
      forecasts: [],
      simulations: [],
      crateForecasts: [],
      crateMeans: [],
      collMeans: [],
      target: [],
      inventories: [],
      washingDays: [],
      crateIOs: [],
      bookings: [],
      isEditingForecast: false,
      isEditingComment: false,
      isCheckingColls: false,
      isCheckingUndecided: false,
      collsInDisplayed: true,
      editableForecast: [],
      editableComment: [],
      bufferText: null,
      displayedColls: [],
      undecidedCollsIn: [],
      undecidedCollsOut: [],
      totalUndecidedIn: 0,
      totalUndecidedOut: 0,
      originalWashed: [],
      editableWashed: [],
      originalRepaired: [],
      editableRepaired: [],
      originalIn: [],
      editableIn: [],
      originalOut: [],
      editableOut: [],
      editDateStart: null,
      editDateEnd: null,
      editBatch: false,
      pinnedData: [],
      file: null,
      fileBuffer: null,
      days: [],
      isImportingExcel: false,
      loadedExcel: false,
      rawSimData: []
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted: async function () {
    if (this.user.group.plants.length === 0) {
      this.plants = await Plant.getAll()
    }
  },
  methods: {
    resetData () {
      this.forecasts = []
      this.crateForecasts = []
      this.crateMeans = []
      this.collMeans = []
      this.crateIOs = []
      this.simulations = []
      this.editableForecast = []
      this.editableComment = []
      this.bufferText = null
      this.displayedColls = []
      this.undecidedCollsIn = []
      this.undecidedCollsOut = []
      this.totalUndecidedIn = 0
      this.totalUndecidedOut = 0
      this.originalWashed = []
      this.target = []
      this.inventories = []
      this.washingDays = []
      this.editableWashed = []
      this.originalRepaired = []
      this.editableRepaired = []
      this.selectedTypes = []
      this.originalIn = []
      this.editableIn = []
      this.originalOut = []
      this.editableOut = []
      this.editDateEnd = null
      this.editDateStart = null
      this.editBatch = false
      this.fileBuffer = null
      this.days = []
      this.rawSimData = []
    },
    editForecast (forecast) {
      this.editableForecast = forecast
      this.editableForecast.types.forEach((type) => {
        let collVals = this.crateIOs[type].filter(x => {
          return new Date(x.collDate).toLocaleDateString('en-CA') === new Date(forecast.date).toLocaleDateString('en-CA')
        })
        this.originalRepaired[type] = this.simulations[forecast.date + '__' + 'repair' + '__' + type] ? this.simulations[forecast.date + '__' + 'repair' + '__' + type] : this.crateMeans[type + '__' + 'repair']
        this.originalWashed[type] = this.simulations[forecast.date + '__' + 'washing' + '__' + type] ? this.simulations[forecast.date + '__' + 'washing' + '__' + type] : this.crateMeans[type + '__' + 'washing'] + this.crateMeans[type + '__' + 'machineWash']
        this.editableRepaired[type] = this.simulations[forecast.date + '__' + 'repair' + '__' + type] ? this.simulations[forecast.date + '__' + 'repair' + '__' + type] : this.crateMeans[type + '__' + 'repair']
        this.editableWashed[type] = this.simulations[forecast.date + '__' + 'washing' + '__' + type] ? this.simulations[forecast.date + '__' + 'washing' + '__' + type] : this.crateMeans[type + '__' + 'washing'] + this.crateMeans[type + '__' + 'machineWash']
        this.originalIn[type] = collVals[0].totalIn
        this.editableIn[type] = collVals[0].totalIn
        this.originalOut[type] = collVals[0].totalOut
        this.editableOut[type] = collVals[0].totalOut
        if (!collVals[0].simIn && new Date(forecast.date) > new Date().setDate(new Date().getDate() + 1) && collVals[0].totalIn < this.collMeans[type]) {
          this.originalIn[type] = this.collMeans[type]
          this.editableIn[type] = this.collMeans[type]
        }
      })
      this.editDateStart = new Date(this.editableForecast.date)
      this.editDateEnd = null
      this.editBatch = false
      this.isEditingForecast = true
    },
    editComment (forecast) {
      this.editableComment = forecast
      if (this.editableComment.comment) {
        this.bufferText = this.editableComment.comment.text
      }
      this.isEditingComment = true
    },
    checkCollects (collects) {
      this.displayedColls = collects
      this.isCheckingColls = true
    },
    async loadFile (file) {
      this.file = file.target.files[0]
      const reader = new FileReader()

      reader.onload = (event) => {
        this.fileBuffer = event.target.result
      }
      await reader.readAsArrayBuffer(this.file)
      this.loadedExcel = true
    },
    chooseFiles: function () {
      document.getElementById('fileUpload').click()
    },
    async testExcel () {
      var readWb = new exceljs.Workbook()
      const excelCollumns = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      await readWb.xlsx.load(this.fileBuffer)
      var display = readWb.getWorksheet('Crate Forecasts')
      var backup = readWb.getWorksheet('DONT TOUCH')
      var currentDate = display.getCell('A1').value
      var out = []
      var index = 0
      while (currentDate && currentDate.length > 0) {
        this.target.types.forEach((type, typeNum) => {
          let washed = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 5)).value
          let backupWashed = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 5)).value
          if (washed !== backupWashed) {
            out.push({
              num_crates: parseInt(washed),
              operation: 'washing',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let repaired = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 6)).value
          let backupRepaired = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 6)).value
          if (repaired !== backupRepaired) {
            out.push({
              num_crates: parseInt(repaired),
              operation: 'repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let inClean = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 7)).value
          let backupInClean = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 7)).value
          if (inClean !== backupInClean) {
            out.push({
              num_crates: parseInt(inClean),
              operation: 'in__clean',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let inDirty = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 8)).value
          let backupInDirty = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 8)).value
          if (inDirty !== backupInDirty) {
            out.push({
              num_crates: parseInt(inDirty),
              operation: 'in__dirty',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let inRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 9)).value
          let backupInRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 9)).value
          if (inRepair !== backupInRepair) {
            out.push({
              num_crates: parseInt(inRepair),
              operation: 'in__repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let outClean = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 10)).value
          let backupOutClean = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 10)).value
          if (outClean !== backupOutClean) {
            out.push({
              num_crates: parseInt(outClean),
              operation: 'out__clean',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let outDirty = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 11)).value
          let backupOutDirty = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 11)).value
          if (outDirty !== backupOutDirty) {
            out.push({
              num_crates: parseInt(outDirty),
              operation: 'out__dirty',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
          let outRepair = display.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 12)).value
          let backupOutRepair = backup.getCell(excelCollumns[typeNum + 1] + (1 + (index * 14) + 12)).value
          if (outRepair !== backupOutRepair) {
            out.push({
              num_crates: parseInt(outRepair),
              operation: 'out__repair',
              id_site: this.storedPlantId,
              date: currentDate,
              type: display.getCell(excelCollumns[typeNum + 1] + ((index * 14) + 2)).value
            })
          }
        })
        index += 1
        currentDate = display.getCell('A' + ((index * 14) + 1)).value
      }
      if (out?.length <= 0) {
        this.$buefy.toast.open({ message: 'No Simulations Found', duration: 10000, type: 'is-warning' })
      }
      this.days = out
    },
    async saveSimulations () {
      this.isLoading = true
      this.$buefy.toast.open({ message: 'Saving Simulations', duration: 10000, type: 'is-success' })
      Forecast.createSimulation(this.days).then(() => {
        this.resetData()
        this.getData()
        this.isImportingExcel = false
        this.isLoading = false
      })
    },
    async exportToExcel () {
      const newWorkbook = new exceljs.Workbook()
      newWorkbook.creator = 'GPS Hub'
      newWorkbook.calcProperties.fullCalcOnLoad = true
      const sheet = newWorkbook.addWorksheet('Crate Forecasts')
      const backup = newWorkbook.addWorksheet('DONT TOUCH')

      const excelCollumns = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      this.forecasts.forEach((forecast, numForecast) => {
        sheet.getCell('A' + (1 + (numForecast * 14) + 0)).value = new Date(forecast.date).toLocaleDateString('en-CA')
        sheet.getCell('B' + (1 + (numForecast * 14) + 0)).value = new Date(forecast.date).toLocaleString('en', { weekday: 'long' })
        sheet.getCell('A' + (1 + (numForecast * 14) + 1)).value = 'Crate Types'
        sheet.getCell('A' + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 2)).value = 'Clean'
        sheet.getCell('A' + (1 + (numForecast * 14) + 2)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 3)).value = 'Dirty'
        sheet.getCell('A' + (1 + (numForecast * 14) + 3)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 4)).value = 'Repair'
        sheet.getCell('A' + (1 + (numForecast * 14) + 4)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 5)).value = 'Washed'
        sheet.getCell('A' + (1 + (numForecast * 14) + 5)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 6)).value = 'Repaired'
        sheet.getCell('A' + (1 + (numForecast * 14) + 6)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 7)).value = 'In Clean'
        sheet.getCell('A' + (1 + (numForecast * 14) + 7)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 8)).value = 'In Dirty'
        sheet.getCell('A' + (1 + (numForecast * 14) + 8)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 9)).value = 'In Repair'
        sheet.getCell('A' + (1 + (numForecast * 14) + 9)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 10)).value = 'Out Clean'
        sheet.getCell('A' + (1 + (numForecast * 14) + 10)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 11)).value = 'Out Dirty'
        sheet.getCell('A' + (1 + (numForecast * 14) + 11)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell('A' + (1 + (numForecast * 14) + 12)).value = 'Out Repair'
        sheet.getCell('A' + (1 + (numForecast * 14) + 12)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        if (!this.washingDays.includes(new Date(forecast.date).getDay())) {
          sheet.getCell('C' + (1 + (numForecast * 14) + 0)).value = 'No Washing Today'
        }

        forecast.types.forEach((type, index) => {
          let extraCratesIn = 0
          if (this.crateIOs[type][numForecast].totalIn < this.collMeans[type]) {
            extraCratesIn = this.collMeans[type] - this.crateIOs[type][numForecast].totalIn
          }
          // Type Name
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 1)).value = type
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 1)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd3d3d3' }
          }
          // First table
          if (numForecast === 0) {
            // Set column widths (only done once)
            let colBuffer = sheet.getColumn('A')
            colBuffer.width = 11
            colBuffer = sheet.getColumn('L')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('N')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('R')
            colBuffer.width = 9
            colBuffer = sheet.getColumn('T')
            colBuffer.width = 10
            colBuffer = sheet.getColumn(excelCollumns[forecast.types.length + 4])
            colBuffer.width = 10
            colBuffer = sheet.getColumn(excelCollumns[forecast.types.length + 5])
            colBuffer.width = 12.5
            // Clean Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 2)).value = forecast.values[type]['CLEAN'].num_crates
            // Dirty Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 3)).value = forecast.values[type]['DIRTY'].num_crates
            // Repair Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 4)).value = forecast.values[type]['REPAIR'].num_crates
          } else {
            // Clean Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 2)).value = { formula: excelCollumns[index + 1] + (((numForecast - 1) * 14) + 3) + '+' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 6) + '+' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 8) + '-' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 11) }
            // Dirty Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 3)).value = { formula: excelCollumns[index + 1] + (((numForecast - 1) * 14) + 4) + '-' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 6) + '+' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 7) + '+' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 9) + '-' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 12) }
            // Repair Stock
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 4)).value = { formula: excelCollumns[index + 1] + (((numForecast - 1) * 14) + 5) + '-' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 7) + '+' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 10) + '-' + excelCollumns[index + 1] + (((numForecast - 1) * 14) + 13) }
          }
          // Washed
          let simWashed = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'washing' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 5)).value = forecast.washed[type] ? forecast.washed[type] : simWashed.length > 0 ? simWashed[0].num_crates : new Date(forecast.date) < new Date().setDate(new Date().getDate() - 5) || !this.washingDays.includes(new Date(forecast.date).getDay()) ? 0 : this.crateMeans[type + '__' + 'washing'] + this.crateMeans[type + '__' + 'machineWash']
          if (simWashed.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 5)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 5)).value = forecast.washed[type] ? forecast.washed[type] : simWashed.length > 0 ? simWashed[0].num_crates : new Date(forecast.date) < new Date().setDate(new Date().getDate() - 5) || !this.washingDays.includes(new Date(forecast.date).getDay()) ? 0 : this.crateMeans[type + '__' + 'washing'] + this.crateMeans[type + '__' + 'machineWash']
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 5)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' }
          }
          // Repaired
          let simRepaired = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'repair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 6)).value = forecast.repaired[type] ? forecast.repaired[type] : simRepaired.length > 0 ? simRepaired[0].num_crates : new Date(forecast.date) < new Date().setDate(new Date().getDate() - 5) || !this.washingDays.includes(new Date(forecast.date).getDay()) ? 0 : this.crateMeans[type + '__' + 'repair']
          if (simRepaired.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 6)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 6)).value = forecast.repaired[type] ? forecast.repaired[type] : simRepaired.length > 0 ? simRepaired[0].num_crates : new Date(forecast.date) < new Date().setDate(new Date().getDate() - 5) || !this.washingDays.includes(new Date(forecast.date).getDay()) ? 0 : this.crateMeans[type + '__' + 'repair']
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 6)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' }
          }
          // In Clean
          let simInClean = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__clean' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 7)).value = simInClean.length > 0 ? simInClean[0].num_crates : this.crateIOs[type][numForecast].cleanIn
          if (simInClean.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 7)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 7)).value = this.crateIOs[type][numForecast].cleanIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 7)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // In Dirty
          let simInDirty = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__dirty' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 8)).value = simInDirty.length > 0 ? simInDirty[0].num_crates : new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) && this.washingDays.includes(new Date(forecast.date).getDay()) ? this.crateIOs[type][numForecast].dirtyIn + extraCratesIn : this.crateIOs[type][numForecast].dirtyIn
          if (simInDirty.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 8)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 8)).value = simInDirty.length > 0 ? simInDirty[0].num_crates : new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) && this.washingDays.includes(new Date(forecast.date).getDay()) ? this.crateIOs[type][numForecast].dirtyIn + extraCratesIn : this.crateIOs[type][numForecast].dirtyIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 8)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // In Repair
          let simInRepair = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'in__repair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 9)).value = simInRepair.length > 0 ? simInRepair[0].num_crates : this.crateIOs[type][numForecast].repairIn
          if (simInRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 9)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 9)).value = simInRepair.length > 0 ? simInRepair[0].num_crates : this.crateIOs[type][numForecast].repairIn
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 9)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'fcd5b4' }
          }
          // Out Clean
          let simOutClean = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__clean' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 10)).value = simOutClean.length > 0 ? simOutClean[0].num_crates : this.crateIOs[type][numForecast].outClean
          if (simOutClean.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 10)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 10)).value = simOutClean.length > 0 ? simOutClean[0].num_crates : this.crateIOs[type][numForecast].outClean
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 10)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
          // Out Dirty
          let simOutDirty = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__dirty' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 11)).value = simOutDirty.length > 0 ? simOutDirty[0].num_crates : this.crateIOs[type][numForecast].outDirty
          if (simOutDirty.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 11)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 11)).value = simOutDirty.length > 0 ? simOutDirty[0].num_crates : this.crateIOs[type][numForecast].outDirty
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 11)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
          // Out Repair
          let simOutRepair = this.rawSimData.filter(x => { return (x.type === type || (type === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && x.operation === 'out__repair' && new Date(x.date).toLocaleDateString('en-ca') === new Date(forecast.date).toLocaleDateString('en-CA') && new Date(forecast.date) > new Date().setDate(new Date().getDate() - 1) })
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 12)).value = simOutRepair.length > 0 ? simOutRepair[0].num_crates : this.crateIOs[type][numForecast].outRepair
          if (simOutRepair.length > 0) {
            sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 12)).font = {
              color: { argb: '4ca04c' }
            }
          }
          backup.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 12)).value = simOutRepair.length > 0 ? simOutRepair[0].num_crates : this.crateIOs[type][numForecast].outRepair
          sheet.getCell(excelCollumns[index + 1] + (1 + (numForecast * 14) + 12)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b7dee8' }
          }
        })
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 1)).value = 'TOTAL'
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 14) + 1)).value = '% MAX'
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 14) + 1)).value = '% MIN'
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 14) + 1)).value = 'TOTAL ALL'
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 14) + 1)).value = '% MAX TOTAL'
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 14) + 1)).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd3d3d3' }
        }
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 2)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 14) + 2) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 14) + 2) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 2] + (1 + (numForecast * 14) + 2)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 2) + '/O1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 14) + 2)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 2) + '/Q1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 14) + 2)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 14) + 2) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 14) + 4) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 5] + (1 + (numForecast * 14) + 2)).value = { formula: excelCollumns[forecast.types.length + 4] + (1 + (numForecast * 14) + 3) + '/M1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 3)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 14) + 3) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 14) + 3) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 14) + 3)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 3) + '/S1*100' }
        sheet.getCell(excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 4)).value = { formula: 'SUMIF(' + 'B' + (1 + (numForecast * 14) + 4) + ':' + excelCollumns[forecast.types.length] + (1 + (numForecast * 14) + 4) + ',">0"' + ')' }
        sheet.getCell(excelCollumns[forecast.types.length + 3] + (1 + (numForecast * 14) + 4)).value = { formula: excelCollumns[forecast.types.length + 1] + (1 + (numForecast * 14) + 4) + '/U1*100' }
        if (forecast.inventory) {
          sheet.getCell(excelCollumns[forecast.types.length + 6] + (1 + (numForecast * 14) + 1)).value = 'INVENTORY TODAY'
          sheet.getCell('A' + (1 + (numForecast * 14) + 0)).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffff00' }
          }
        }
        if (forecast.comment) {
          sheet.getCell(excelCollumns[forecast.types.length + 7] + (1 + (numForecast * 14) + 1)).value = forecast.comment?.text
          backup.getCell(excelCollumns[forecast.types.length + 7] + (1 + (numForecast * 14) + 1)).value = forecast.comment?.text
        }
        if (numForecast === 0) {
          sheet.getCell('L1').value = 'Max Total'
          sheet.getCell('M1').value = this.target.max_total
          backup.getCell('M1').value = this.target.max_total
          sheet.getCell('N1').value = 'Max Clean'
          sheet.getCell('O1').value = this.target.max_clean
          backup.getCell('O1').value = this.target.max_clean
          sheet.getCell('P1').value = 'Min Clean'
          sheet.getCell('Q1').value = this.target.min_clean
          backup.getCell('Q1').value = this.target.min_clean
          sheet.getCell('R1').value = 'Min Dirty'
          sheet.getCell('S1').value = this.target.min_dirty
          backup.getCell('S1').value = this.target.min_dirty
          sheet.getCell('T1').value = 'Min Repair'
          sheet.getCell('U1').value = this.target.min_repair
          backup.getCell('U1').value = this.target.min_repair
        }
      })
      var buffer = await newWorkbook.xlsx.writeBuffer('exportedData.xlsx')
      saveAs(new Blob([buffer]), this.selectedPlant.name + '-' + this.selectedPlant.id + new Date().toLocaleDateString('en-CA') + '.xlsx')
    },
    async submitComment (archive) {
      if (this.editableComment.comment && (this.editableComment.comment.text !== this.bufferText)) {
        Forecast.updateComment({
          id_site: this.editableComment.comment.id_site,
          date: this.editableComment.comment.date,
          text: this.bufferText,
          id: this.editableComment.comment.id
        }).then(() => {
          this.resetData()
          this.getData()
          this.isEditingComment = false
        })
      }
      if (!this.editableComment.comment && (this.bufferText && this.bufferText.length > 0)) {
        Forecast.createComment({
          id_site: this.storedPlantId,
          date: this.editableComment.date,
          text: this.bufferText
        }).then(() => {
          this.resetData()
          this.getData()
          this.isEditingComment = false
        })
      }
      if (this.editableComment.comment && archive === true) {
        Forecast.deleteComment({
          id: this.editableComment.comment.id
        }).then(() => {
          this.resetData()
          this.getData()
          this.isEditingComment = false
        })
      }
    },
    async submitEdit () {
      var outSimulations = []
      if (!this.editBatch) {
        this.editDateEnd = this.editDateStart
      }
      try {
        for (var d = new Date(this.editDateStart); d <= this.editDateEnd; d.setDate(d.getDate() + 1)) {
          this.editableForecast.types.forEach((type) => {
            if (parseInt(this.editableWashed[type]) !== this.originalWashed[type]) {
              outSimulations.push(
                {
                  num_crates: parseInt(this.editableWashed[type]),
                  operation: 'washing',
                  id_site: this.storedPlantId,
                  date: d.toLocaleDateString('en-CA'),
                  type: type
                }
              )
            }
            if (parseInt(this.editableRepaired[type]) !== this.originalRepaired[type]) {
              outSimulations.push(
                {
                  num_crates: parseInt(this.editableRepaired[type]),
                  operation: 'repair',
                  id_site: this.storedPlantId,
                  date: d.toLocaleDateString('en-CA'),
                  type: type
                }
              )
            }
            if (parseInt(this.editableIn[type]) !== this.originalIn[type]) {
              outSimulations.push(
                {
                  num_crates: parseInt(this.editableIn[type]),
                  operation: 'in',
                  id_site: this.storedPlantId,
                  date: d.toLocaleDateString('en-CA'),
                  type: type
                }
              )
            }
            if (parseInt(this.editableOut[type]) !== this.originalOut[type]) {
              outSimulations.push(
                {
                  num_crates: parseInt(this.editableOut[type]),
                  operation: 'out',
                  id_site: this.storedPlantId,
                  date: d.toLocaleDateString('en-CA'),
                  type: type
                }
              )
            }
          })
        }
        Forecast.createSimulation(outSimulations).then(() => {
          this.editableForecast = null
          this.originalForecast = null
          this.resetData()
          this.getData()
          this.isEditingForecast = false
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    pinData () {
      this.pinnedData.push({
        crateForecast: this.crateForecasts.filter(x => x.type === this.activeTab),
        storedDate: this.storedDate,
        key: this.pinnedData.length
      })
    },
    closeInfoCard () {
      this.infoCard = false
    },
    async resetSimulations () {
      await Forecast.resetSimulations({
        id_site: this.selectedPlant.id
      })
      this.resetData()
      this.getData()
    },
    async getData () {
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      this.forecasts = []
      this.crateForecasts = []
      this.crateMeans = []
      this.collMeans = []
      this.crateIOs = []
      this.bookings = []
      this.target = []
      this.inventories = []
      this.washingDays = []
      this.storedPlantId = this.selectedPlant.id
      this.storedName = this.selectedPlant.name
      let commentData = null
      this.$buefy.toast.open({ message: 'Loading Data', duration: 10000, type: 'is-success' })
      try {
        const data = await Forecast.get({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        commentData = await Forecast.getComments({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        this.inventories = data.inventories && data.inventories.length > 0 ? data.inventories : []
        this.washingDays = data.washingDays && data.washingDays.length > 0 ? data.washingDays : [1, 2, 3, 4, 5]
        this.target = (data.targetStock && data.targetStock.length > 0) ? {
          id: data.targetStock[0].id,
          id_site: this.selectedPlant.id,
          max_dirty: data.targetStock[0].max_dirty,
          min_dirty: data.targetStock[0].min_dirty,
          max_clean: data.targetStock[0].max_clean,
          min_clean: data.targetStock[0].min_clean,
          max_total: data.targetStock[0].max_total,
          min_repair: data.targetStock[0].min_repair,
          min_available: data.targetStock[0].min_available,
          types: JSON.parse(data.targetStock[0].types)
        } : {
          id_site: this.selectedPlant.id,
          max_dirty: 0,
          min_dirty: 0,
          max_clean: 0,
          min_clean: 0,
          max_total: 0,
          min_repair: 0,
          min_available: 0,
          types: [{ name: '5.2' }]
        }
        this.siteTypes = this.target.types
        this.selectedTypes = this.siteTypes
        const simData = await Forecast.getSimulation({
          start: this.dateStart.toLocaleDateString('en-CA'),
          end: this.dateEnd.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id,
          types: this.siteTypes.map(x => x.name)
        })
        this.rawSimData = simData
        let simLimit = new Date()
        simLimit.setDate(simLimit.getDate() + 1)
        simData.forEach((element) => {
          if (new Date(element.date).toLocaleDateString('en-CA') >= simLimit.toLocaleDateString('en-CA')) {
            if (this.siteTypes.map(x => x.name).includes('5.X') && element.type === '5.2') {
              this.simulations[new Date(element.date).toLocaleDateString('en-CA') + '__' + element.operation + '__' + '5.X'] = element.num_crates
            }
            this.simulations[new Date(element.date).toLocaleDateString('en-CA') + '__' + element.operation + '__' + element.type] = element.num_crates
          }
        })
        if (this.siteTypes.map(x => x.name).includes('5.X')) {
          this.collMeans = data.meanColl
          this.collMeans['5.X'] = this.collMeans['5.2'] + this.collMeans['5.5']
        } else {
          this.collMeans = data.meanColl
        }
        data.forecasts.forEach((element) => {
          var dayMoves = data.movements.filter((x) => { return (new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA') && x.movements.length > 0) })
          this.crateForecasts.push({
            ...element,
            movements: dayMoves.length > 0 ? dayMoves.reduce((combined, current) => {
              return combined.concat(current.movements)
            }, []) : null
          })
        })
        if (this.siteTypes.map(x => x.name).includes('5.X')) {
          data.means.forEach((element) => {
            if (element.type === '5.2' || element.type === '5.5') {
              if (this.crateMeans['5.X' + '__' + element.operation]) {
                this.crateMeans['5.X' + '__' + element.operation] += element.mean
              } else {
                this.crateMeans['5.X' + '__' + element.operation] = element.mean
              }
            } else {
              this.crateMeans[element.type + '__' + element.operation] = element.mean
            }
          })
        } else {
          data.means.forEach((element) => {
            this.crateMeans[element.type + '__' + element.operation] = element.mean
          })
        }

        var endCollLoop = new Date(this.dateEnd).setDate(this.dateEnd.getDate() + 1)
        var collTypes = this.target.types.map(x => x.name)
        this.undecidedCollsIn = await data.collects.filter(x => {
          return (collTypes.includes(x.type) || (collTypes.includes('5.X') && (x.type === '5.2' || x.type === '5.5'))) && x.date === null && x.done_date === null && (x.shipType === 'TRFI' || x.shipType === 'COL' || x.shipType === 'NCR')
        })
        if (this.undecidedCollsIn.length > 0) {
          this.totalUndecidedIn = this.undecidedCollsIn.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
        }
        this.undecidedCollsOut = await data.collects.filter(x => {
          return (collTypes.includes(x.type) || (collTypes.includes('5.X') && (x.type === '5.2' || x.type === '5.5'))) && x.date === null && x.done_date === null && (x.shipType === 'TRFO' || x.shipType === 'DEL')
        })
        if (this.undecidedCollsOut.length > 0) {
          this.totalUndecidedOut = this.undecidedCollsOut.reduce((sum, element) => sum + parseInt(element.num_crates), 0)
        }
        /* eslint-disable no-unmodified-loop-condition */
        collTypes.forEach(colltype => {
          this.crateIOs[colltype] = []
          for (var startCollLoop = new Date(this.dateStart); startCollLoop <= endCollLoop; startCollLoop.setDate(startCollLoop.getDate() + 1)) {
            let simIn = false
            let simOut = false
            let predColls = data.collects.filter(x => {
              return !x.done_date && new Date(x.date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5')))
            })
            let doneColls = data.collects.filter(x => {
              return x.done_date && new Date(x.done_date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5')))
            })
            let totalIn = 0
            let cleanIn = 0
            let dirtyIn = 0
            let repairIn = 0
            if (simData.filter(x => {
              return x.operation.split('__')[0] === 'in' && new Date(x.date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && new Date(x.date) > simLimit
            }).length > 0) {
              simIn = true
              totalIn = simData.filter(x => {
                return x.operation.split('__')[0] === 'in' && new Date(x.date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5')))
              })[0].num_crates
            } else {
              totalIn = predColls.filter(x => {
                return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              cleanIn = predColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'CLEAN'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'CLEAN'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              dirtyIn = predColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'DIRTY'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'DIRTY'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              repairIn = predColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'REPAIR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR') && x.status === 'REPAIR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            }
            let totalOut = 0
            let outClean = 0
            let outDirty = 0
            let outRepair = 0
            if (simData.filter(x => {
              return x.operation.split('__')[0] === 'out' && new Date(x.date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5'))) && new Date(x.date) > simLimit
            }).length > 0) {
              simOut = true
              totalOut = simData.filter(x => {
                return x.operation.split('__')[0] === 'out' && new Date(x.date).toLocaleDateString('en-CA') === startCollLoop.toLocaleDateString('en-CA') && (x.type === colltype || (colltype === '5.X' && (x.type === '5.2' || x.type === '5.5')))
              })[0].num_crates
            } else {
              outClean = predColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'CLEAN'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'CLEAN'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              outDirty = predColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'DIRTY'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'DIRTY'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              outRepair = predColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'REPAIR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return (x.shipType === 'DEL' || x.shipType === 'TRFO') && x.status === 'REPAIR'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
              totalOut = predColls.filter(x => {
                return x.shipType === 'DEL' || x.shipType === 'TRFO'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0) + doneColls.filter(x => {
                return x.shipType === 'DEL' || x.shipType === 'TRFO'
              }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            }
            this.crateIOs[colltype].push({
              collDate: startCollLoop.toLocaleDateString('en-CA'),
              totalIn: totalIn,
              cleanIn: cleanIn,
              dirtyIn: dirtyIn,
              repairIn: repairIn,
              totalOut: totalOut,
              outClean: outClean,
              outDirty: outDirty,
              outRepair: outRepair,
              predCollsIn: predColls.filter(x => {
                return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
              }),
              predCollsOut: predColls.filter(x => {
                return x.shipType === 'DEL' || x.shipType === 'TRFO'
              }),
              doneCollsIn: doneColls.filter(x => {
                return x.shipType === 'COL' || x.shipType === 'TRFI' || x.shipType === 'NCR'
              }),
              doneCollsOut: doneColls.filter(x => {
                return x.shipType === 'DEL' || x.shipType === 'TRFO'
              }),
              simIn: simIn,
              simOut: simOut
            })
          }
        })
        /* eslint-enable no-unmodified-loop-condition */
      } catch (error) {
        this.$buefy.toast.open({ message: 'Error loading data:' + error.message, duration: 10000, type: 'is-warning' })
      }
      this.crateForecasts.forEach(element => {
        let types = [...new Set(element.forecasts.map(x => {
          return x.status.split('__')[0]
        }))]
        if (collTypes.includes('5.X')) {
          types.splice(types.indexOf('5.2'), 1)
          types.splice(types.indexOf('5.5'), 1)
          types.push('5.X')
        }
        let statuses = [...new Set(element.forecasts.map(x => {
          return x.status.split('__')[1]
        }))]
        let values = []
        let moves = element.movements
        let washed = []
        let repaired = []
        let total = 0
        let dirty = 0
        let clean = 0
        let repair = 0
        let available = 0
        let inventory = false
        let comments = commentData.filter(x => new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA'))
        types.forEach(type => {
          values[type] = []
          washed[type] = !moves ? null : moves.filter((x) => {
            return (x.operation === 'washing' || x.operation === 'machineWash') && (x.status_crate_start.split('__')[0] === type || (type === '5.X' && (x.status_crate_start.split('__')[0] === '5.2' || x.status_crate_start.split('__')[0] === '5.5')))
          }).reduce((sum, move) => sum + move.num_crates, 0)
          repaired[type] = !moves ? null : moves.filter((x) => {
            return x.operation === 'repair' && (x.status_crate_start.split('__')[0] === type || (type === '5.X' && (x.status_crate_start.split('__')[0] === '5.2' || x.status_crate_start.split('__')[0] === '5.5')))
          }).reduce((sum, move) => sum + move.num_crates, 0)
          statuses.forEach(status => {
            let buffer = []
            if (type === '5.X') {
              buffer = element.forecasts.filter((x) => {
                return x.status === '5.2' + '__' + status || x.status === '5.5' + '__' + status
              })
              buffer = {
                changed: buffer[0].changed && buffer[1].changed,
                collChanged: buffer[0].collChanged && buffer[1].collChanged,
                num_crates: buffer[0].num_crates + buffer[1].num_crates,
                status: '5.X' + '__' + status
              }
            } else {
              buffer = element.forecasts.filter((x) => {
                return x.status === type + '__' + status
              })[0]
            }
            values[type][status] = {
              num_crates: buffer.num_crates,
              changed: buffer.changed
            }
            if (this.inventories.filter((x) => {
              return new Date(x.date).toLocaleDateString('en-CA') === new Date(element.date).toLocaleDateString('en-CA')
            }).length > 0) {
              inventory = true
            }
            if (buffer.num_crates > 0) {
              total += buffer.num_crates
              if (status === 'CLEAN' || status === 'DIRTY') {
                available += buffer.num_crates
              }
              if (status === 'CLEAN') {
                clean += buffer.num_crates
              } else if (status === 'DIRTY') {
                dirty += buffer.num_crates
              } else if (status === 'REPAIR') {
                repair += buffer.num_crates
              }
            }
          })
        })
        let allColls = []
        let combinedColls = {
          doneCollsIn: [],
          doneCollsOut: [],
          predCollsIn: [],
          predCollsOut: []
        }
        this.selectedTypes.forEach(x => {
          allColls = allColls.concat(this.crateIOs[x.name].filter(x => x.collDate === new Date(element.date).toLocaleDateString('en-CA')))
        })
        allColls.forEach(coll => {
          combinedColls.doneCollsIn = combinedColls.doneCollsIn.concat(coll.doneCollsIn)
          combinedColls.doneCollsOut = combinedColls.doneCollsOut.concat(coll.doneCollsOut)
          combinedColls.predCollsIn = combinedColls.predCollsIn.concat(coll.predCollsIn)
          combinedColls.predCollsOut = combinedColls.predCollsOut.concat(coll.predCollsOut)
        })
        this.forecasts.push({
          date: new Date(element.date).toLocaleDateString('en-CA'),
          types: types,
          statuses: statuses,
          values: values,
          washed: washed,
          repaired: repaired,
          total: total,
          dirty: dirty,
          clean: clean,
          repair: repair,
          available: available,
          inventory: inventory,
          comment: comments && comments.length > 0 ? comments[0] : null,
          dayMoves: combinedColls
        })
      })
      this.isLoading = false
    }
  }
}

</script>
